import React, {useCallback, useRef} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import type {OnApproveData} from '@paypal/paypal-js/types';
import {PayPalButtons, PayPalScriptProvider} from '@paypal/react-paypal-js';

import {connect} from 'react-redux';

import {CHECKOUT_ERRORS} from 'constants/analytics/events/checkout';
import {PlacingOrderScreen} from 'desktop/components/placingOrderScreen/placingOrderScreen';
import {usePayPalButtonHandlers} from 'desktop/hooks/payPal/usePayPalButtonHandlers';
import {ApplicationStoreType} from 'desktop/store';
import {handlePayPalDesktopErrorAnalytic} from 'features/expressPayments/payPalV3/utils/analytics';

import {callbackWithAnalyticsEvent} from 'libs/analytics/callbackWithAnalytic';
import {logWithSentry} from 'libs/sentry/sentry';

import {savePayPalExpress} from '../../store/reducers/payments/paypalExpress/paypalExpress';
import {getTokenPayPalExpress} from '../../store/sagas/payments/paypalExpress/payPalExpress';
import {getCart} from '../../store/selectors';
import {getPayPalExpressLoading} from '../../store/selectors/payments/payPalExpress/payPalExpress';
import {CartType} from '../../types';

import buttonStyle from './buttonStyle';

import s from './paypalButtonV3.scss';

export type PropsType = {
    clientId: string;
    name: string;
    cart: CartType;
    loading: boolean;
    onClick(): void;
    save(params: {token: string; id: string}): void;
};

/**
 * Payments Methods for checkout
 */
export const PayPalButtonV3 = ({cart, clientId, loading, name, onClick, save}: PropsType) => {
    const sessionIdRef = useRef('');
    // used in createOrder for always getting latest card by reference
    const cartRef = useRef(cart);
    cartRef.current = cart;
    const paypalButtonHandlers = usePayPalButtonHandlers(sessionIdRef, 'cart');

    const onApprove = useCallback(
        async (data: OnApproveData) => {
            if (!sessionIdRef.current) return;
            await callbackWithAnalyticsEvent('place order', 'PayPal', async () => {
                save({
                    id: sessionIdRef.current,
                    token: data.orderID,
                });
            });
        },
        [save],
    );

    const createOrder = useCallback(async () => {
        const {id, token} = await getTokenPayPalExpress(cartRef.current);
        if (!token || !id) {
            sendDesktopAnalyticsEvent(CHECKOUT_ERRORS, {
                action: 'PayPal',
                message: 'Initialization error',
                step: 'Paypal V3 Cart',
            });
            logWithSentry(new Error('PaypalV3: initialization'), 'PaypalV3: Error in SDK');
            return '';
        }
        sessionIdRef.current = id;
        return token;
    }, []);

    return (
        <div data-exp={name}>
            <PayPalScriptProvider options={{clientId, components: 'buttons'}}>
                <PayPalButtons
                    createOrder={createOrder}
                    onClick={onClick}
                    onError={error => handlePayPalDesktopErrorAnalytic(error, true)}
                    {...paypalButtonHandlers}
                    className={s.container}
                    onApprove={onApprove}
                    style={buttonStyle}
                />
            </PayPalScriptProvider>
            {loading && <PlacingOrderScreen />}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (state: ApplicationStoreType) => ({
    cart: getCart(state),
    loading: getPayPalExpressLoading(state),
});

/**
 * Paypal button v3 connected to cart reducer
 */
export const PayPalButtonV3Connected = connect(mapStateToProps, {
    save: savePayPalExpress,
})(PayPalButtonV3);
